import { useSyncExternalStore } from 'react';
import { alertMissed } from './alerts';
import { announceHere } from './changes';
import { useMessagesLive, type LiveEvent } from './messages';

/**
 * Calls as they end (1.12.4). Stitch tells the people whose call it was the moment a call ends (a
 * 'calls' event on their live stream): their call history (My calls, Calls, Home) loads again by
 * itself, and a call to them that nobody answered and that didn't reach voicemail shows a notice,
 * "Missed call from …", with Call back. While Stitch isn't in front, a notification says it too.
 */
export interface MissedNotice {
  tenant: string;
  callId: string;
  extension: string;
  /** Who called: their name, or their number as people write it. */
  caller: string;
  /** Their number (an extension when it's a coworker), to call back. */
  number: string;
  at: string;
}

let notices: MissedNotice[] = [];
const listeners = new Set<() => void>();
const MAX_NOTICES = 3;

function set(next: MissedNotice[]): void {
  notices = next;
  for (const l of [...listeners]) l();
}

export function useMissedNotices(): MissedNotice[] {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => notices,
    () => notices
  );
}

export function dismissMissed(callId: string): void {
  set(notices.filter((n) => n.callId !== callId));
}

/** For the company's people while Stitch is open: call history that follows, and missed calls. */
export function useMissedLive(tenant: string, on: boolean): void {
  const t = tenant.toLowerCase();
  useMessagesLive((e) => {
    if (e.type !== 'calls' || e.tenant?.toLowerCase() !== t) return;
    // The call history on screen follows (only screens showing calls load again).
    announceHere({ tenant: t, topic: 'calls' });
    if (e.missed && e.callId) missed(t, e);
  }, on && Boolean(t));
}

function missed(t: string, e: LiveEvent): void {
  const m = e.missed!;
  if (notices.some((n) => n.callId === e.callId && n.tenant === t)) return;
  set([{ tenant: t, callId: e.callId!, extension: m.extension, caller: m.caller || 'Unknown caller', number: m.number, at: m.at }, ...notices].slice(0, MAX_NOTICES));
  void alertMissed(e);
}
